import React, { useEffect, useState } from 'react';
import { NavBar } from '@/components/navbar';
import { Helmet } from 'react-helmet-async';
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { regulationsData } from '@/data/regulations3';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/components/ui/use-toast';
import { API_CONFIG } from '@/constants/env';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

declare global {
  interface Window {
    sharedAiApps?: string[];
  }
}

interface AIApplication {
  name: string;
  description: string;
}

interface ImplementationStep {
  step: number;
  name: string;
  description: string;
  estimatedTime: number;
  requiredPeople: number;
  priority: string;
  effortScale: string;
  skillsRequired: string[];
  dependencies: number[];
  aiApplications: AIApplication[];
}

interface Regulation {
  name: string;
  abbreviation: string;
  overview: string;
  countries: string[];
  requirements: string;
  applicableIndustries: string[];
  applicableCompanySizes: string[];
  recommendedTeamSize: number;
  implementationSteps: ImplementationStep[];
}

// Dropdown component
const Dropdown = ({ label, options, value, onChange, className = '' }) => {
  const hasSelection = !!value; // Check if a value is selected

  return (
    <div className={`mb-4 ${className}`}>
      <label className="block text-sm font-medium mb-1 text-gray-700">{label}</label>
      <select
        value={value}
        onChange={onChange}
        className={`w-full p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#229302] ${
          hasSelection
            ? 'bg-[#e8f5e9] border-2 border-[#229302] text-[#184c06]'
            : 'border border-gray-300 text-gray-700'
        }`}
      >
        <option value="">Select {label}</option>
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

// Customization Panel Component
const CustomizationPanel = ({ filters, setFilters, resetAll, filteredRegulations }) => {
  // Get all available countries
  const baseCountries = [
    ...new Set(regulationsData.regulations.flatMap(r => r.countries || [])),
  ].sort();

  // Add Europe and ensure Austria is included in the list
  let countryList = ['Europe', ...baseCountries];

  // Add Austria if it's not already in the list
  if (!countryList.includes('Austria')) {
    countryList.push('Austria');
    countryList.sort();
    // Keep Europe at the top after sorting
    countryList = ['Europe', ...countryList.filter(c => c !== 'Europe')];
  }

  // Get industries based on selected country
  const getFilteredIndustries = () => {
    if (filters.country) {
      let countryRegulations;

      if (filters.country === 'Europe') {
        // European countries list
        const europeanCountries = [
          'Austria',
          'Belgium',
          'Bulgaria',
          'Croatia',
          'Cyprus',
          'Czech Republic',
          'Denmark',
          'Estonia',
          'Finland',
          'France',
          'Germany',
          'Greece',
          'Hungary',
          'Ireland',
          'Italy',
          'Latvia',
          'Lithuania',
          'Luxembourg',
          'Malta',
          'Netherlands',
          'Poland',
          'Portugal',
          'Romania',
          'Slovakia',
          'Slovenia',
          'Spain',
          'Sweden',
          'United Kingdom',
          'Switzerland',
          'Norway',
        ];

        // Get regulations that apply to any European country
        countryRegulations = regulationsData.regulations.filter(
          r => r.countries && r.countries.some(country => europeanCountries.includes(country))
        );
      } else {
        // For European countries, also include regulations that apply to "All EU Member States"
        const europeanCountries = [
          'Austria',
          'Belgium',
          'Bulgaria',
          'Croatia',
          'Cyprus',
          'Czech Republic',
          'Denmark',
          'Estonia',
          'Finland',
          'France',
          'Germany',
          'Greece',
          'Hungary',
          'Ireland',
          'Italy',
          'Latvia',
          'Lithuania',
          'Luxembourg',
          'Malta',
          'Netherlands',
          'Poland',
          'Portugal',
          'Romania',
          'Slovakia',
          'Slovenia',
          'Spain',
          'Sweden',
        ];

        if (europeanCountries.includes(filters.country)) {
          countryRegulations = regulationsData.regulations.filter(
            r =>
              (r.countries && r.countries.includes(filters.country)) ||
              (r.countries && r.countries.includes('All EU Member States')) ||
              (r.regions && r.regions.includes('All EU Member States'))
          );
        } else {
          // Only show industries from regulations that include the selected country
          countryRegulations = regulationsData.regulations.filter(
            r => r.countries && r.countries.includes(filters.country)
          );
        }
      }

      return [...new Set(countryRegulations.flatMap(r => r.applicableIndustries || []))].sort();
    } else {
      // Show all industries if no country is selected
      return [
        ...new Set(regulationsData.regulations.flatMap(r => r.applicableIndustries || [])),
      ].sort();
    }
  };

  const filteredIndustries = getFilteredIndustries();

  // Employee count options instead of company sizes
  const employeeSizes = [
    '1-50 employees',
    '51-250 employees',
    '251-1000 employees',
    '1001+ employees',
  ];

  // Team size options
  const teamSizes = ['1-2', '3-5', '6-10', '10+'];
  
  // Salary range options
  const salaryRanges = [
    '$50K - $75K',
    '$75K - $100K',
    '$100K - $125K',
    '$125K - $150K',
  ];

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-lg font-bold mb-4 text-[#184c06]">Filter</h2>

      <Dropdown
        label="Country"
        options={countryList}
        value={filters.country}
        onChange={e => {
          // Reset industry when country changes to avoid invalid selections
          const newValue = e.target.value;
          setFilters({
            ...filters,
            country: newValue,
            industry: '', // Clear industry selection when country changes
          });
        }}
      />

      <Dropdown
        label="Industry"
        options={filteredIndustries}
        value={filters.industry}
        onChange={e => setFilters({ ...filters, industry: e.target.value })}
      />

      <Dropdown
        label="Company Size"
        options={employeeSizes}
        value={filters.companySize}
        onChange={e => setFilters({ ...filters, companySize: e.target.value })}
      />

      <Dropdown
        label="Average Salary Range"
        options={salaryRanges}
        value={filters.salaryRange}
        onChange={e => setFilters({ ...filters, salaryRange: e.target.value })}
      />

      <Dropdown
        label="Compliance Team Size"
        options={teamSizes}
        value={filters.teamSize}
        onChange={e => setFilters({ ...filters, teamSize: e.target.value })}
      />

      <button
        onClick={resetAll}
        className="w-full mt-4 p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
      >
        Reset
      </button>
    </div>
  );
};

// Create a Share Card component
const ShareCard = ({ generateShareUrl, shareUrl }) => {
  return (
    <div className="p-4 bg-white rounded-lg shadow mt-4">
      <h2 className="text-lg font-bold mb-4 text-[#184c06]">Share</h2>
      <button
        onClick={generateShareUrl}
        className="w-full p-3 bg-[#229302] text-white font-medium rounded-md hover:bg-[#1b6504] transition-colors"
      >
        Generate Share Link
      </button>

      {shareUrl && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 mb-2">Your share link:</p>
          <div className="flex items-center">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="w-full p-2 text-xs bg-gray-50 border border-gray-300 rounded"
              onClick={e => e.target.select()}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Link copied to clipboard! Share this link to show your current selections.
          </p>
        </div>
      )}
    </div>
  );
};

// We don't need this component anymore - we'll use a simpler approach directly in the components

// Regulations List Component
const RegulationsList = ({ regulations, selectedRegulation, setSelectedRegulation }) => {
  const isMobile = useIsMobile();
  const [expandedRegulationId, setExpandedRegulationId] = useState<string | null>(null);

  // Toggle regulation selection (select if different, deselect if same)
  const toggleRegulation = regulation => {
    if (selectedRegulation?.name === regulation.name) {
      // If clicking on the currently selected regulation, deselect it
      setSelectedRegulation(null);
    } else {
      // Otherwise, select the clicked regulation
      setSelectedRegulation(regulation);
    }
  };
  
  // Toggle expanded view for regulation details on mobile
  const toggleExpanded = (e, abbreviation) => {
    if (isMobile) {
      e.stopPropagation(); // Prevent triggering the parent onClick
      setExpandedRegulationId(prevId => 
        prevId === abbreviation ? null : abbreviation
      );
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-[#184c06]">Regulations</h2>
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#e8f5e9] text-[#1b6504] text-sm font-medium">
          {regulations.length}
        </span>
      </div>

      {regulations.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200 max-w-xs mx-auto">
            <h3 className="font-medium text-gray-700 mb-2">No Matching Regulations</h3>
            <p className="text-gray-500 mb-3">
              We couldn't find any regulations that match your current filter criteria.
            </p>
            <p className="text-sm text-gray-400">
              Try adjusting your filters or click the "Reset" button to see all available
              regulations.
            </p>
          </div>
        </div>
      ) : (
        <ul className="space-y-2 flex-grow overflow-auto">
          {regulations.map((regulation, index) => {
            const isExpanded = isMobile 
              ? expandedRegulationId === regulation.abbreviation
              : expandedRegulationId === regulation.abbreviation;

            return (
              <li
                key={index}
                className={`rounded-md cursor-pointer transition-all duration-300 ${
                  selectedRegulation?.name === regulation.name
                    ? 'bg-[#e8f5e9] border-2 border-[#229302]'
                    : 'bg-gray-50 border border-gray-200 hover:bg-[#e6f0fa]'
                } ${isExpanded ? 'pb-5' : ''}`}
                onClick={() => toggleRegulation(regulation)}
                onMouseEnter={!isMobile ? () => setExpandedRegulationId(regulation.abbreviation) : undefined}
                onMouseLeave={!isMobile ? () => setExpandedRegulationId(null) : undefined}
              >
                <div className="p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">{regulation.abbreviation}</h3>
                      <p className="text-sm text-gray-600">{regulation.name}</p>
                    </div>
                    
                    {isMobile && (
                      <button 
                        className="ml-2 text-gray-500 p-1" 
                        onClick={(e) => toggleExpanded(e, regulation.abbreviation)}
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="content-wrapper mt-3 animate-['fade-in_0.5s_ease-in']">
                      <h4 className="text-xs font-semibold text-gray-700 mt-2">Requirements:</h4>
                      <p className="text-xs text-gray-600 mt-1">{regulation.requirements}</p>

                      <div className="mt-2">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            selectedRegulation?.name === regulation.name
                              ? 'bg-[#e0f2e9] text-[#229302]'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {regulation.applicableCompanySizes?.join(', ')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Manual Steps Component
const ManualSteps = ({ steps, selectedSteps, setSelectedSteps }) => {
  const isMobile = useIsMobile();
  const [expandedStepId, setExpandedStepId] = useState<number | null>(null);

  const toggleStep = step => {
    if (selectedSteps.includes(step.step)) {
      setSelectedSteps(selectedSteps.filter(s => s !== step.step));
    } else {
      setSelectedSteps([...selectedSteps, step.step]);
    }
  };
  
  // Toggle expanded view for step details on mobile
  const toggleExpanded = (e, stepId) => {
    if (isMobile) {
      e.stopPropagation(); // Prevent triggering the parent onClick
      setExpandedStepId(prevId => 
        prevId === stepId ? null : stepId
      );
    }
  };

  // Handle Select All functionality
  const areAllStepsSelected =
    steps.length > 0 && steps.every(step => selectedSteps.includes(step.step));

  const selectAllSteps = () => {
    if (areAllStepsSelected) {
      // If all are already selected, deselect all
      setSelectedSteps([]);
    } else {
      // Add all steps that aren't already selected
      setSelectedSteps(steps.map(step => step.step));
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-[#184c06]">Manual Steps</h2>
        {steps.length > 0 && (
          <button
            onClick={selectAllSteps}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${
              areAllStepsSelected
                ? 'bg-[#229302] text-white border-[#229302] hover:bg-[#1b6504]'
                : 'bg-[#e8f5e9] text-[#229302] border-[#229302] hover:bg-[#d4edda]'
            }`}
            disabled={steps.length === 0}
            title={areAllStepsSelected ? 'Deselect All' : 'Select All'}
          >
            {areAllStepsSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {!steps || steps.length === 0 ? (
        <div className="flex items-center justify-center ">
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200 max-w-xs mx-auto">
            <h3 className="font-medium text-gray-700 mb-2">Implementation Steps</h3>
            <p className="text-gray-500 text-sm mb-3">
              Select a regulation to see the implementation steps required for compliance.
            </p>
            <p className="text-sm text-gray-400">
              These steps represent the manual processes that your team would need to complete for
              regulatory compliance.
            </p>
          </div>
        </div>
      ) : (
        <ul className="space-y-2 flex-grow overflow-auto">
          {steps.map(step => {
            const isExpanded = isMobile 
              ? expandedStepId === step.step
              : expandedStepId === step.step;

            return (
              <li
                key={step.step}
                className={`rounded-md cursor-pointer transition-all duration-300 ${
                  selectedSteps.includes(step.step)
                    ? 'bg-[#e8f5e9] border-2 border-[#229302]'
                    : 'bg-gray-50 border border-gray-200 hover:bg-[#e6f0fa]'
                } ${isExpanded ? 'pb-5' : ''}`}
                onClick={() => toggleStep(step)}
                onMouseEnter={!isMobile ? () => setExpandedStepId(step.step) : undefined}
                onMouseLeave={!isMobile ? () => setExpandedStepId(null) : undefined}
              >
                <div className="p-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-medium text-sm max-w-[65%]">
                      {step.step}. {step.name}
                    </h3>
                    <div className="flex items-center">
                      {(step.effortScale === 'High' || step.effortScale === 'Medium') && (
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            step.effortScale === 'High'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {step.effortScale}
                        </span>
                      )}
                      
                      {isMobile && (
                        <button 
                          className="ml-2 text-gray-500 p-1" 
                          onClick={(e) => toggleExpanded(e, step.step)}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="content-wrapper mt-1 animate-['fade-in_0.5s_ease-in']">
                      <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {step.skillsRequired.map((skill, i) => (
                          <span
                            key={i}
                            className={`text-xs px-2 py-1 rounded ${
                              selectedSteps.includes(step.step)
                                ? 'bg-[#e0f2e9] text-[#229302]'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">{step.description}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// AI Applications Component
const AIApplications = ({
  aiApps,
  selectedSteps,
  starredApps,
  toggleStarredApp,
  updateMultipleStarredApps,
}) => {
  const isMobile = useIsMobile();
  // Flatten all AI applications from selected steps
  const allApps = aiApps.filter(app => selectedSteps.includes(app.stepId));
  const [expandedAppId, setExpandedAppId] = useState(null);

  // Toggle expanded view for app details on mobile
  const toggleExpanded = (e, appName) => {
    if (isMobile) {
      e.stopPropagation(); // Prevent triggering the parent onClick
      setExpandedAppId(prevId => 
        prevId === appName ? null : appName
      );
    }
  };

  // Handle Select All functionality
  const areAllAppsSelected =
    allApps.length > 0 && allApps.every(app => starredApps.some(a => a.name === app.name));

  const selectAllAiApps = () => {
    // If all are already selected, deselect all, otherwise select all
    updateMultipleStarredApps(allApps, !areAllAppsSelected);
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-[#184c06]">AI Processes</h2>
        {allApps.length > 0 && (
          <button
            onClick={selectAllAiApps}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${
              areAllAppsSelected
                ? 'bg-[#229302] text-white border-[#229302] hover:bg-[#1b6504]'
                : 'bg-[#e8f5e9] text-[#229302] border-[#229302] hover:bg-[#d4edda]'
            }`}
            disabled={allApps.length === 0}
            title={areAllAppsSelected ? 'Deselect All' : 'Select All'}
          >
            {areAllAppsSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {allApps.length === 0 ? (
        <div className="flex items-center justify-center ">
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200 max-w-xs mx-auto">
            <h3 className="font-medium text-gray-700 mb-2">AI Optimization</h3>
            <p className="text-gray-500 mb-3 text-sm">
              Select manual steps to view potential AI-powered automation opportunities.
            </p>
            <p className="text-sm text-gray-400">
              Each AI process can significantly reduce the time, resources, and cost required for
              compliance implementation.
            </p>
          </div>
        </div>
      ) : (
        <ul className="space-y-2 flex-grow overflow-auto">
          {allApps.map((app, index) => {
            const isExpanded = isMobile 
              ? expandedAppId === app.name
              : expandedAppId === app.name;
            const isSelected = starredApps.some(a => a.name === app.name);

            return (
              <li
                key={index}
                onClick={() => toggleStarredApp(app)}
                className={`rounded-md cursor-pointer transition-all duration-300 ${
                  isSelected
                    ? 'bg-[#e8f5e9] border-2 border-[#229302]'
                    : 'bg-gray-50 border border-gray-200 hover:bg-[#e6f0fa]'
                } ${isExpanded ? 'pb-5' : ''}`}
                onMouseEnter={!isMobile ? () => setExpandedAppId(app.name) : undefined}
                onMouseLeave={!isMobile ? () => setExpandedAppId(null) : undefined}
              >
                <div className="p-3">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">{app.name}</h3>
                    {isMobile && (
                      <button 
                        className="ml-2 text-gray-500 p-1" 
                        onClick={(e) => toggleExpanded(e, app.name)}
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    )}
                  </div>

                  {isExpanded ? (
                    <div className="content-wrapper mt-1 animate-['fade-in_0.5s_ease-in']">
                      <p className="text-xs text-gray-600 mt-1">{app.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            isSelected ? 'bg-[#e0f2e9] text-[#229302]' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          Step: {app.stepId}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            isSelected ? 'bg-[#e0f2e9] text-[#229302]' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          AI-Powered
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">{app.description}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Metrics Panel Component
const MetricsPanel = ({
  starredApps,
  regulation,
  generateShareUrl,
  filters,
  selectedSteps,
  shareUrl,
  showShareUrl,
}) => {
  const hasStarredApps = starredApps.length > 0;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to handle the Book Demo button click
  const handleBookDemo = async () => {
    if (!hasStarredApps) return;

    // First make sure we have a share URL
    let currentShareUrl = shareUrl;
    if (!currentShareUrl) {
      try {
        // Create share URL manually if we don't have one
        const shareData = {
          filters: filters,
          regulationId: regulation?.abbreviation || '',
          steps: selectedSteps,
          aiApps: starredApps.map(app => app.name),
        };

        // Convert to base64
        const encodedData = btoa(JSON.stringify(shareData));

        // Generate URL with encoded data
        currentShareUrl = `${window.location.origin}${window.location.pathname}?share=${encodedData}`;
      } catch (e) {
        console.error('Error generating share URL:', e);
        currentShareUrl = window.location.href;
      }
    }

    setIsSubmitting(true);

    try {
      // Prepare metrics data
      const metricsData = {
        timeSavedDays: totalTimeSavedDays,
        costSavings: totalCostSaved,
        resourceOptimization: totalPeopleSaved,
        implementationTime: Math.ceil(implementationTime / 8),
        aiEfficiencyGain: aiEfficiencyGain,
      };

      // Prepare the payload
      const payload = {
        share_url: currentShareUrl,
        selected_regulation: regulation?.abbreviation || '',
        selected_steps: selectedSteps,
        selected_ai_apps: starredApps.map(app => app.name),
        metrics: metricsData,
        country: filters.country,
        industry: filters.industry,
        company_size: filters.companySize,
      };

      // Send the data to the backend API
      console.log('Sending data to API:', payload);

      const response = await fetch(`${API_CONFIG.API_BASE_URL}/regulations-navigator/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Handle the response
      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Error parsing response:', e);
        // If backend is not available, fall back to default behavior
        if (!response.ok) {
          throw new Error(`Server returned status ${response.status}`);
        }
        data = {
          success: response.ok,
          message: 'Request processed',
          redirect: '/book-demo',
        };
      }

      if (data.success) {
        toast({
          title: 'Demo request submitted!',
          description: 'Redirecting to the demo booking page...',
          variant: 'success',
        });

        // Redirect to the demo page
        setTimeout(() => {
          window.location.href = '/book-demo';
        }, 1500);
      } else {
        throw new Error(data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Something went wrong',
        description: 'Could not submit your request. Please try again later.',
        variant: 'destructive',
      });

      // Still redirect to book-demo in case of error
      setTimeout(() => {
        window.location.href = '/book-demo';
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate metrics based on the proportion of selected AI processes
  const calculateProportionalMetrics = () => {
    // If no regulation selected, return zeroes
    if (!regulation) {
      return {
        manualTime: 0,
        implementationTime: 0,
        timeSavedHours: 0,
        timeSavedDays: 0,
        peopleSaved: 0,
        costSaved: 0,
        efficiencyGain: 0,
        manualCost: 0,
        basePeopleNeeded: 0,
        baseImplementationDays: 0
      };
    }
    
    // Get the total manual time for all selected steps
    let manualTime = 0;
    let totalRequiredPeople = 0;
    let totalSelectableApps = 0;
    
    // Get team size as a number from the filter value
    let teamSizeNum = 0;
    if (filters.teamSize) {
      const teamSizeParts = filters.teamSize.split('-');
      // If range like "3-5", use average
      if (teamSizeParts.length > 1) {
        teamSizeNum = (parseInt(teamSizeParts[0]) + parseInt(teamSizeParts[1])) / 2;
      } else {
        // For "10+" format
        teamSizeNum = parseInt(teamSizeParts[0].replace('+', ''));
      }
    } else {
      // Default team size if none selected
      teamSizeNum = 3;
    }
    
    // Calculate base metrics from selected steps
    selectedSteps.forEach(stepId => {
      const step = regulation.implementationSteps.find(s => s.step === stepId);
      if (step) {
        manualTime += step.estimatedTime;
        totalRequiredPeople += step.requiredPeople;
        
        // Count how many AI processes are available for this step
        const availableApps = step.aiApplications?.length || 0;
        totalSelectableApps += availableApps;
      }
    });
    
    // Base metrics even without AI
    // Convert hours to days (assuming 8-hour workdays)
    const baseImplementationDays = Math.ceil(manualTime / 8);
    
    // Calculate base cost for manual implementation ($150 per hour)
    const manualCost = manualTime * 150;
    
    // Calculate people needed above optimal team size
    const basePeopleNeeded = Math.max(0, Math.round(totalRequiredPeople - teamSizeNum));
    
    // If no AI apps are selected, return base metrics
    if (!hasStarredApps) {
      return {
        manualTime,
        implementationTime: manualTime,
        timeSavedHours: 0,
        timeSavedDays: 0,
        peopleSaved: 0,
        costSaved: 0,
        efficiencyGain: 0,
        manualCost,
        basePeopleNeeded,
        baseImplementationDays
      };
    }
    
    // We'll calculate resource optimization (people) directly from the selection ratio too
    let totalRequiredPeopleAcrossAllSteps = 0;
    
    // First, calculate the total people required across all selected steps
    selectedSteps.forEach(stepId => {
      const step = regulation.implementationSteps.find(s => s.step === stepId);
      if (step) {
        totalRequiredPeopleAcrossAllSteps += step.requiredPeople;
      }
    });
    
    // Directly proportional calculation based on the number of AI processes selected
    const aiProcessCount = starredApps.length;
    const totalPossibleApps = totalSelectableApps;
    
    // The maximum possible people savings (60% of total required people)
    const maxPeopleSavings = totalRequiredPeopleAcrossAllSteps * 0.6;
    
    // Calculate people saved directly proportional to the number of AI processes
    const selectionRatio = totalPossibleApps > 0 ? aiProcessCount / totalPossibleApps : 0;
    const totalPeopleSaved = maxPeopleSavings * selectionRatio;
    
    // Calculate implementation time with direct proportion to AI processes selected
    let implementationTime;
    
    if (aiProcessCount === 0) {
      // No AI processes - no time reduction
      implementationTime = manualTime;
    } else {
      // Maximum possible time reduction (85% of manual time)
      const maxReduction = 0.85;
      
      // Calculate reduction percentage directly proportional to selection ratio
      // Each AI process contributes equally to the overall reduction
      const reductionPercentage = maxReduction * selectionRatio;
      
      // Apply the reduction to get implementation time
      implementationTime = manualTime * (1 - reductionPercentage);
    }
    
    // Time saved is the difference between manual time and AI-assisted time
    const finalTimeSaved = manualTime - implementationTime;
    
    // Convert hours to days (assuming 8-hour workdays)
    const totalTimeSavedDays = Math.ceil(finalTimeSaved / 8);
    
    // Calculate cost saved ($150 per hour)
    const totalCostSaved = finalTimeSaved * 150;
    
    // Calculate efficiency gain - directly proportional to the number of AI processes selected
    // This is simply the selection ratio as a percentage
    const aiEfficiencyGain = Math.floor(selectionRatio * 100);
      
    return {
      manualTime,
      implementationTime,
      timeSavedHours: finalTimeSaved,
      timeSavedDays: totalTimeSavedDays,
      peopleSaved: Math.round(totalPeopleSaved),
      costSaved: totalCostSaved,
      efficiencyGain: aiEfficiencyGain,
      manualCost,
      basePeopleNeeded,
      baseImplementationDays
    };
  };
  
  // Get all the metrics
  const metrics = calculateProportionalMetrics();
  
  // Extract the values we need for display
  const manualTime = metrics.manualTime;
  const implementationTime = metrics.implementationTime;
  const totalTimeSavedHours = metrics.timeSavedHours;
  const totalTimeSavedDays = metrics.timeSavedDays;
  const totalPeopleSaved = metrics.peopleSaved;
  const totalCostSaved = metrics.costSaved;
  const aiEfficiencyGain = metrics.efficiencyGain;
  const manualCost = metrics.manualCost;
  const basePeopleNeeded = metrics.basePeopleNeeded;
  const baseImplementationDays = metrics.baseImplementationDays;

  // Determine active state based on selections
  const hasSteps = selectedSteps.length > 0;
  
  // Common card styling
  const getCardStyle = (hasAiApps, hasSteps) => {
    if (hasAiApps) return 'p-3 bg-[#f1f8e9] rounded-md'; // Green for AI selection
    if (hasSteps) return 'p-3 bg-gray-100 rounded-md'; // Gray for only steps selected
    return 'p-3 bg-gray-100 rounded-md'; // Default gray
  };

  // Common text styling
  const getTextStyle = (hasAiApps, hasSteps) => {
    if (hasAiApps) return 'text-2xl font-bold text-[#1b6504]'; // Green text for AI selection
    if (hasSteps) return 'text-2xl font-bold text-gray-600'; // Dark gray for steps
    return 'text-2xl font-bold text-gray-400'; // Light gray for neither
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-[#184c06]">Metrics</h2>
      </div>

      <div className="space-y-4">
        <div className={getCardStyle(hasStarredApps, hasSteps)}>
          <h3 className="font-medium">Time Saved</h3>
          <p className={getTextStyle(hasStarredApps, hasSteps)}>
            {hasStarredApps 
              ? `${totalTimeSavedDays} days` 
              : hasSteps && regulation 
                ? '0 days' 
                : '--'}
          </p>
          {hasSteps && regulation && !hasStarredApps && (
            <p className="text-sm text-gray-600">Potential savings with AI</p>
          )}
        </div>

        <div className={getCardStyle(hasStarredApps, hasSteps)}>
          <h3 className="font-medium">Cost Savings</h3>
          <p className={getTextStyle(hasStarredApps, hasSteps)}>
            {hasStarredApps 
              ? `$${totalCostSaved.toLocaleString()}` 
              : hasSteps && regulation 
                ? `$0` 
                : '--'}
          </p>
          {hasSteps && regulation && !hasStarredApps && (
            <p className="text-sm text-gray-600">Base cost: ${manualCost.toLocaleString()}</p>
          )}
        </div>

        <div className={getCardStyle(hasStarredApps, hasSteps)}>
          <h3 className="font-medium">Resource Optimization</h3>
          <p className={getTextStyle(hasStarredApps, hasSteps)}>
            {hasStarredApps 
              ? `${totalPeopleSaved} FTE` 
              : hasSteps && regulation 
                ? `${basePeopleNeeded} FTE needed` 
                : '--'}
          </p>
          {hasSteps && regulation && !hasStarredApps && (
            <p className="text-sm text-gray-600">Additional staff required</p>
          )}
        </div>

        <div className={getCardStyle(hasStarredApps, hasSteps)}>
          <h3 className="font-medium">Implementation Time</h3>
          <p className={getTextStyle(hasStarredApps, hasSteps)}>
            {hasStarredApps 
              ? `${Math.ceil(implementationTime / 8)} days` 
              : hasSteps && regulation 
                ? `${baseImplementationDays} days` 
                : '--'}
          </p>
          {regulation && hasStarredApps && (
            <p className="text-sm text-gray-600">
              (vs. {baseImplementationDays} days manually)
            </p>
          )}
          {hasSteps && regulation && !hasStarredApps && (
            <p className="text-sm text-gray-600">Manual implementation time</p>
          )}
        </div>

        <div className={getCardStyle(hasStarredApps, hasSteps)}>
          <h3 className="font-medium">AI Efficiency Gain</h3>
          <p className={getTextStyle(hasStarredApps, hasSteps)}>
            {hasStarredApps 
              ? `${aiEfficiencyGain}%` 
              : hasSteps && regulation
                ? '0%' 
                : '0%'}
          </p>
          {hasSteps && regulation && !hasStarredApps && (
            <p className="text-sm text-gray-600">Select AI processes to optimize</p>
          )}
        </div>

        <button
          className={`w-full p-3 mt-4 font-medium rounded-md transition-colors ${
            hasStarredApps
              ? isSubmitting
                ? 'bg-[#1b6504] text-white cursor-wait'
                : 'bg-[#229302] text-white hover:bg-[#1b6504] cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          onClick={hasStarredApps && !isSubmitting ? handleBookDemo : undefined}
          disabled={!hasStarredApps || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Book Free Consultation'}
        </button>
        <span className="text-xs text-gray-400">Book a 30-min free consultant</span>

        <div className="mt-8">
          <button
            className="w-full p-3 font-medium rounded-md transition-colors border border-[#229302] text-[#229302] bg-white hover:bg-[#f1f8e9] cursor-pointer"
            onClick={generateShareUrl}
          >
            Generate Share Link
          </button>
          <span className="text-xs text-gray-400 block mb-2">Share your selections with others</span>
          
          {showShareUrl && shareUrl && (
            <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200 transition-all duration-300 ease-in-out opacity-100 max-h-[200px]">
              <p className="text-xs font-medium text-gray-700 mb-2">Your share link:</p>
              <div className="flex flex-col">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="w-full p-2 text-xs bg-white border border-gray-300 rounded mb-2"
                  onClick={e => e.target.select()}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-600">✓ Copied to clipboard</span>
                  <button 
                    className="text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => setShowShareUrl(false)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MinimalLanding = () => {
  const isMobile = useIsMobile();
  
  // State
  const [filters, setFilters] = useState({
    industry: '',
    country: '',
    companySize: '51-250 employees',
    teamSize: '6-10',
    salaryRange: '$75K - $100K',
  });

  const [filteredRegulations, setFilteredRegulations] = useState<Regulation[]>([]);
  const [selectedRegulation, setSelectedRegulation] = useState<Regulation | null>(null);
  const [selectedSteps, setSelectedSteps] = useState<number[]>([]);
  const [aiApplications, setAIApplications] = useState<any[]>([]);
  const [starredApps, setStarredApps] = useState<any[]>([]);
  const [shareUrl, setShareUrl] = useState<string>('');
  
  // Mobile tab state
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showTabGuide, setShowTabGuide] = useState<boolean>(false);

  // State for share link display timeout
  const [showShareUrl, setShowShareUrl] = useState<boolean>(false);
  
  // Function to generate share URL
  const generateShareUrl = () => {
    // Create object with all current selections
    const shareData = {
      filters: filters,
      regulationId: selectedRegulation?.abbreviation || '',
      steps: selectedSteps,
      aiApps: starredApps.map(app => app.name),
    };

    // Convert to base64
    const encodedData = btoa(JSON.stringify(shareData));

    // Generate URL with encoded data
    const url = `${window.location.origin}${window.location.pathname}?share=${encodedData}`;
    setShareUrl(url);
    setShowShareUrl(true);

    // Copy to clipboard
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast({
          title: 'Share link copied to clipboard!',
          description: 'You can now share this link with others.',
          variant: 'success',
        });
      })
      .catch(err => {
        console.error('Could not copy URL: ', err);
        toast({
          title: 'Share link generated',
          description: "But couldn't copy to clipboard automatically.",
          variant: 'default',
        });
      });
      
    // Set a timeout to hide the URL after 30 seconds
    setTimeout(() => {
      setShowShareUrl(false);
    }, 30000);
  };

  // Mobile tab guide component
  const TabGuideDialog = () => {
    return (
      <Dialog open={showTabGuide} onOpenChange={setShowTabGuide}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Welcome to Mobile View</DialogTitle>
            <DialogDescription>
              This interface is optimized for mobile using tabs.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 space-y-3 ">
            <p className="text-sm">Swipe through the tabs to navigate between:</p>
            <ul className="list-disc pl-5 text-sm space-y-2">
              <li><span className="font-medium">Filter</span> - Set your requirements</li>
              <li><span className="font-medium">Regulations</span> - Select applicable regulations</li>
              <li><span className="font-medium">Manual Steps</span> - Choose implementation steps</li>
              <li><span className="font-medium">AI Processes</span> - Select automation opportunities</li>
              <li><span className="font-medium">Metrics</span> - View savings & request demo</li>
            </ul>
            <p className="text-sm mt-4">Tap the tab names at the bottom to jump directly to a section.</p>
            <button 
              onClick={() => setShowTabGuide(false)}
              className="w-full mt-4 p-3 bg-[#229302] text-white font-medium rounded-md"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Parse URL parameters on load
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const shareParam = queryParams.get('share');

    if (shareParam) {
      try {
        // Decode the share data
        const shareData = JSON.parse(atob(shareParam));

        // Apply filters
        if (shareData.filters) {
          setFilters(shareData.filters);
        }

        // Find and set the regulation
        if (shareData.regulationId) {
          const regulation = regulationsData.regulations.find(
            r => r.abbreviation === shareData.regulationId
          );
          if (regulation) {
            setSelectedRegulation(regulation);
          }
        }

        // Set selected steps
        if (shareData.steps) {
          setSelectedSteps(shareData.steps);
        }

        // Set starred AI apps (we'll need to match them once steps are selected)
        if (shareData.aiApps && shareData.aiApps.length > 0) {
          // We'll use this in the aiApplications useEffect
          window.sharedAiApps = shareData.aiApps;
        }
      } catch (error) {
        console.error('Error parsing share URL: ', error);
      }
    }

    // Always load regulations
    setFilteredRegulations(regulationsData.regulations);
    
    // Show the tab guide on mobile after a short delay
    if (isMobile) {
      const hasSeenGuide = localStorage.getItem('seen-mobile-tabs-guide');
      if (!hasSeenGuide) {
        setTimeout(() => {
          setShowTabGuide(true);
          localStorage.setItem('seen-mobile-tabs-guide', 'true');
        }, 1000);
      }
    }
  }, [isMobile]);

  // Reset everything
  const resetAll = () => {
    setFilters({
      industry: '',
      country: '',
      companySize: '',
      teamSize: '',
      salaryRange: '',
    });
    setSelectedRegulation(null);
    setSelectedSteps([]);
    setAIApplications([]);
    setStarredApps([]);
  };

  // Filter regulations based on selected criteria
  useEffect(() => {
    let filtered = [...regulationsData.regulations];

    if (filters.industry) {
      filtered = filtered.filter(
        r => r.applicableIndustries && r.applicableIndustries.includes(filters.industry)
      );
    }

    if (filters.country) {
      if (filters.country === 'Europe') {
        // European countries list
        const europeanCountries = [
          'Austria',
          'Belgium',
          'Bulgaria',
          'Croatia',
          'Cyprus',
          'Czech Republic',
          'Denmark',
          'Estonia',
          'Finland',
          'France',
          'Germany',
          'Greece',
          'Hungary',
          'Ireland',
          'Italy',
          'Latvia',
          'Lithuania',
          'Luxembourg',
          'Malta',
          'Netherlands',
          'Poland',
          'Portugal',
          'Romania',
          'Slovakia',
          'Slovenia',
          'Spain',
          'Sweden',
          'United Kingdom',
          'Switzerland',
          'Norway',
        ];

        // Filter for regulations that apply to any European country or All EU Member States
        filtered = filtered.filter(
          r =>
            (r.countries && r.countries.some(country => europeanCountries.includes(country))) ||
            (r.countries && r.countries.includes('All EU Member States')) ||
            (r.regions && r.regions.includes('All EU Member States'))
        );
      } else {
        // For European countries, also include regulations that apply to "All EU Member States"
        const europeanCountries = [
          'Austria',
          'Belgium',
          'Bulgaria',
          'Croatia',
          'Cyprus',
          'Czech Republic',
          'Denmark',
          'Estonia',
          'Finland',
          'France',
          'Germany',
          'Greece',
          'Hungary',
          'Ireland',
          'Italy',
          'Latvia',
          'Lithuania',
          'Luxembourg',
          'Malta',
          'Netherlands',
          'Poland',
          'Portugal',
          'Romania',
          'Slovakia',
          'Slovenia',
          'Spain',
          'Sweden',
        ];

        if (europeanCountries.includes(filters.country)) {
          filtered = filtered.filter(
            r =>
              (r.countries && r.countries.includes(filters.country)) ||
              (r.countries && r.countries.includes('All EU Member States')) ||
              (r.regions && r.regions.includes('All EU Member States'))
          );
        } else {
          // Normal country filtering for non-EU countries
          filtered = filtered.filter(r => r.countries && r.countries.includes(filters.country));
        }
      }
    }

    // For company size, map employee count ranges to original regulation size categories
    if (filters.companySize) {
      const sizeMap = {
        '1-50 employees': ['Small', 'Micro'],
        '51-250 employees': ['Medium', 'Small'],
        '251-1000 employees': ['Large', 'Medium'],
        '1001+ employees': ['Enterprise', 'Large'],
      };

      const applicableSizes = sizeMap[filters.companySize] || [];
      filtered = filtered.filter(
        r =>
          r.applicableCompanySizes &&
          r.applicableCompanySizes.some(size => applicableSizes.includes(size))
      );
    }

    // Team size filter is removed as per requirements - it should not filter regulations

    setFilteredRegulations(filtered);

    // Reset selected regulation if it's no longer in filtered results
    if (selectedRegulation && !filtered.some(r => r.name === selectedRegulation.name)) {
      setSelectedRegulation(null);
      setSelectedSteps([]);
    }
  }, [filters, selectedRegulation]);

  // Process AI applications when steps are selected
  useEffect(() => {
    if (selectedRegulation) {
      // Collect all AI applications from selected steps
      const apps = selectedRegulation.implementationSteps
        .filter(step => selectedSteps.includes(step.step))
        .flatMap(step =>
          (step.aiApplications || []).map(app => ({
            ...app,
            stepId: step.step,
            estimatedTimeSaved: Math.floor(step.estimatedTime * 0.3),
            estimatedPeopleSaved: Math.ceil(step.requiredPeople * 0.3),
          }))
        );

      setAIApplications(apps);

      // Check if we have shared AI apps to restore
      if (window.sharedAiApps && window.sharedAiApps.length > 0) {
        // Find matching apps in the current available apps
        const sharedAppsToStar = apps.filter(app => window.sharedAiApps.includes(app.name));

        if (sharedAppsToStar.length > 0) {
          setStarredApps(sharedAppsToStar);
          // Clear the shared apps so we don't apply them again
          window.sharedAiApps = undefined;
        }
      } else {
        // Normal behavior - remove stars from apps that are no longer in the selected steps
        setStarredApps(prev => prev.filter(app => apps.some(a => a.name === app.name)));
      }
    } else {
      setAIApplications([]);
      setStarredApps([]);
    }
  }, [selectedSteps, selectedRegulation]);

  // Toggle starred app
  const toggleStarredApp = app => {
    if (starredApps.some(a => a.name === app.name)) {
      setStarredApps(starredApps.filter(a => a.name !== app.name));
    } else {
      setStarredApps([...starredApps, app]);
    }
  };

  // Add or remove multiple apps at once
  const updateMultipleStarredApps = (apps, shouldBeStarred) => {
    if (shouldBeStarred) {
      // Add all the apps that aren't already starred
      const appsToAdd = apps.filter(app => !starredApps.some(a => a.name === app.name));
      if (appsToAdd.length > 0) {
        setStarredApps([...starredApps, ...appsToAdd]);
      }
    } else {
      // Remove all the specified apps that are currently starred
      setStarredApps(starredApps.filter(a => !apps.some(app => app.name === a.name)));
    }
  };

  // Mobile tab navigation component
  const MobileTabNav = () => {
    const tabNames = ['Filter', 'Regulations', 'Steps', 'AI', 'Metrics'];
    
    // Handle swipe gestures
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    
    const handleTouchStart = (e) => {
      setTouchStart(e.targetTouches[0].clientX);
    };
    
    const handleTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };
    
    const handleTouchEnd = () => {
      if (touchStart - touchEnd > 75) {
        // Swipe left, go to next tab
        setActiveTab(prev => Math.min(prev + 1, 4));
      }
      
      if (touchEnd - touchStart > 75) {
        // Swipe right, go to previous tab
        setActiveTab(prev => Math.max(prev - 1, 0));
      }
    };
    
    return (
      <div className="flex flex-col h-[calc(100vh-190px)]">

        
        {/* Tab navigation hints */}
        <div className="flex justify-between items-center mb-3 px-2 pt-16">
          <div className="flex space-x-1">
            {tabNames.map((_, index) => (
              <div 
                key={index} 
                className={`h-1 rounded-full ${index === activeTab ? 'bg-[#229302] w-8' : 'bg-gray-200 w-6'}`} 
              />
            ))}
          </div>
          
          <div className="text-xs text-gray-400 flex items-center">
            {activeTab > 0 && (
              <span className="mr-1">← Swipe</span>
            )}
            {activeTab < 4 && (
              <span className="ml-1">Swipe →</span>
            )}
          </div>
        </div>
        
        {/* Tab content container */}
        <div className="flex-1 overflow-hidden mb-14">
          <div 
            className="h-full"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Filter Tab */}
            {activeTab === 0 && (
              <div className="h-full overflow-y-auto pt-2">
                <div className="bg-white rounded-lg shadow">
                  <CustomizationPanel
                    filters={filters}
                    setFilters={setFilters}
                    resetAll={resetAll}
                    filteredRegulations={filteredRegulations}
                  />
                </div>
              </div>
            )}
            
            {/* Regulations Tab */}
            {activeTab === 1 && (
              <div className="h-full overflow-y-auto pt-2">
                <RegulationsList
                  regulations={filteredRegulations}
                  selectedRegulation={selectedRegulation}
                  setSelectedRegulation={setSelectedRegulation}
                />
              </div>
            )}
            
            {/* Steps Tab */}
            {activeTab === 2 && (
              <div className="h-full overflow-y-auto pt-2">
                <ManualSteps
                  steps={selectedRegulation?.implementationSteps || []}
                  selectedSteps={selectedSteps}
                  setSelectedSteps={setSelectedSteps}
                />
              </div>
            )}
            
            {/* AI Tab */}
            {activeTab === 3 && (
              <div className="h-full overflow-y-auto pt-2">
                <AIApplications
                  aiApps={aiApplications}
                  selectedSteps={selectedSteps}
                  starredApps={starredApps}
                  toggleStarredApp={toggleStarredApp}
                  updateMultipleStarredApps={updateMultipleStarredApps}
                />
              </div>
            )}
            
            {/* Metrics Tab */}
            {activeTab === 4 && (
              <div className="h-full overflow-y-auto pt-2">
                <MetricsPanel
                  starredApps={starredApps}
                  regulation={selectedRegulation}
                  generateShareUrl={generateShareUrl}
                  filters={filters}
                  selectedSteps={selectedSteps}
                  shareUrl={shareUrl}
                  showShareUrl={showShareUrl}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Tab navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-between z-10">
          {tabNames.map((name, index) => (
            <button
              key={index}
              className={`flex-1 py-3 text-xs font-medium ${
                activeTab === index
                  ? 'text-[#229302] border-t-2 border-[#229302]'
                  : 'text-gray-500'
              }`}
              onClick={() => setActiveTab(index)}
            >
              {name}
              {index === 1 && filteredRegulations.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-[#e8f5e9] text-[#1b6504]">
                  {filteredRegulations.length}
                </span>
              )}
              {index === 2 && selectedSteps.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-[#e8f5e9] text-[#1b6504]">
                  {selectedSteps.length}
                </span>
              )}
              {index === 3 && starredApps.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-[#e8f5e9] text-[#1b6504]">
                  {starredApps.length}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Help icon for reopening guide */}
        <button 
          className="fixed bottom-20 right-4 bg-[#229302] text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-10"
          onClick={() => setShowTabGuide(true)}
        >
          ?
        </button>
      </div>
    );
  };

  // Check for server-side rendering to avoid hydration issues
  useEffect(() => {
    // Force a re-render after the component mounts to get accurate mobile detection
    if (isMobile === undefined) {
      const timer = setTimeout(() => {
        setActiveTab(0); // Just to trigger a re-render
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);
  
  return (
    <div className="min-h-screen flex flex-col font-sans overflow-x-hidden bg-gray-100">
      <PageViewTracker pageName="regulations-navigator" />
      <Helmet>
        <title>Compliance Regulations Navigator | Verdea</title>
        <meta
          name="description"
          content="Interactive tool to navigate through regulatory requirements and optimize compliance with AI."
        />
      </Helmet>

      {/* Toast container for notifications */}
      <Toaster />

      {/* Tab guide modal for mobile */}
      {isMobile && <TabGuideDialog />}

      {/* Navbar */}
      <header>
        <NavBar landingMode={true} />
      </header>

      {/* Main content */}
      <main className={`flex-grow ${isMobile ? 'p-2' : 'p-4'}`}>
        <div className={`mx-auto ${isMobile ? 'pt-6 w-full' : 'pt-16 max-w-7xl'}`}>
          {isMobile ? (
            <MobileTabNav />
          ) : (
            <div className="grid grid-cols-5 gap-4 h-[calc(100vh-120px)]">
              {/* Column 1: Customization */}
              <div className="col-span-1 h-full overflow-auto">
                <div className="space-y-4">
                  <CustomizationPanel
                    filters={filters}
                    setFilters={setFilters}
                    resetAll={resetAll}
                    filteredRegulations={filteredRegulations}
                  />
                </div>
              </div>

              {/* Column 2: Regulations */}
              <div className="col-span-1 h-full overflow-auto">
                <RegulationsList
                  regulations={filteredRegulations}
                  selectedRegulation={selectedRegulation}
                  setSelectedRegulation={setSelectedRegulation}
                />
              </div>

              {/* Column 3: Manual Steps */}
              <div className="col-span-1 h-full overflow-auto">
                <ManualSteps
                  steps={selectedRegulation?.implementationSteps || []}
                  selectedSteps={selectedSteps}
                  setSelectedSteps={setSelectedSteps}
                />
              </div>

              {/* Column 4: AI Applications */}
              <div className="col-span-1 h-full overflow-auto">
                <AIApplications
                  aiApps={aiApplications}
                  selectedSteps={selectedSteps}
                  starredApps={starredApps}
                  toggleStarredApp={toggleStarredApp}
                  updateMultipleStarredApps={updateMultipleStarredApps}
                />
              </div>

              {/* Column 5: Metrics */}
              <div className="col-span-1 h-full overflow-auto">
                <MetricsPanel
                  starredApps={starredApps}
                  regulation={selectedRegulation}
                  generateShareUrl={generateShareUrl}
                  filters={filters}
                  selectedSteps={selectedSteps}
                  shareUrl={shareUrl}
                  showShareUrl={showShareUrl}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MinimalLanding;
